import { useQuery } from '@tanstack/react-query';

import { apiRequest } from 'helpers/api';
import { logException } from 'helpers/loggers';

import type { OnboardingFlow } from 'types/restaurant';

export const ONBOARDING_FLOW_QUERY_KEY = 'onboardingFlow';

type AdminRestaurantResponse = {
  data: { onboarding_flow?: OnboardingFlow };
};

/**
 * Reads whether a restaurant onboards through the self-serve wizard or the sales-led flow. Only the
 * admin API's restaurant detail carries it, and a missing value is `legacy`, as on the backend.
 */
export const useOnboardingFlow = (restaurantId: string | undefined) =>
  useQuery<OnboardingFlow>({
    queryKey: [ONBOARDING_FLOW_QUERY_KEY, restaurantId],
    queryFn: async () => {
      const { data, error } = await apiRequest<AdminRestaurantResponse>({
        path: `restaurant/${restaurantId}`,
        customHost: import.meta.env.VITE_ADMIN_API_HOST as string,
      });

      if (error) {
        logException({
          message: 'Failed to fetch the onboarding flow',
          error,
          context: { restaurantId },
          tags: null,
        });
        throw error;
      }

      return data?.data.onboarding_flow ?? 'legacy';
    },
    enabled: Boolean(restaurantId),
    staleTime: Infinity,
  });
